// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type FetchArgs } from '@reduxjs/toolkit/query';
import { produce } from 'immer';

const QUERY_OR_HASH = /[?#]/;

type QueryParams = Record<string, any>;

/** RTK Query endpoint details included in normalization warnings. */
export interface NormalizeLogContext {
  /**
   * Endpoint name from the RTK Query api, such as
   * `unshareAdvisoryUsersAccountsRaw`.
   */
  endpoint?: string;

  /** `query` or `mutation`. */
  type?: string;

  /** HTTP method from the request args. */
  method?: string;

  /** URL from the endpoint definition, before normalization. */
  url?: string;
}

interface RemovedQueryParam {
  key: string;
  value: unknown;
}

const formatEndpoint = (context?: NormalizeLogContext): string => {
  if (!context?.endpoint) {
    return 'unknown';
  }

  return context.type
    ? `"${context.endpoint}" (${context.type})`
    : `"${context.endpoint}"`;
};

const warnNormalize = (message: string, details: Record<string, unknown>) => {
  console.warn(`[fetchQuery] ${message}`, details);
};

/**
 * Values that must not be written into the query string.
 *
 * `0` and `false` stay. `null`, `undefined`, blank strings, `NaN`, and empty arrays
 * are dropped so `fetchBaseQuery` does not append a trailing `?`.
 */
export const isEmptyQueryParamValue = (value: unknown): boolean => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return true;
  }

  if (typeof value === 'string') {
    return value.trim() === '';
  }

  if (Array.isArray(value)) {
    return value.length === 0 || value.every(isEmptyQueryParamValue);
  }

  return false;
};

interface SanitizeQueryParamsOptions {
  /** When false, empty params are dropped without a console warning. */
  warn?: boolean;

  /** Endpoint that sent the params. Included in the warning. */
  context?: NormalizeLogContext;
}

const partitionQueryParams = (
  params: QueryParams,
): { cleaned: QueryParams; removed: RemovedQueryParam[] } => {
  const removed: RemovedQueryParam[] = [];

  const cleaned = produce(params, draft => {
    Object.keys(draft).forEach(key => {
      if (!isEmptyQueryParamValue(draft[key])) {
        return;
      }

      removed.push({ key, value: params[key] });
      delete draft[key];
    });
  });

  return { cleaned, removed };
};

const warnEmptyParams = (
  removed: RemovedQueryParam[],
  context?: NormalizeLogContext,
) => {
  const keys = removed.map(param => param.key).join(', ');

  warnNormalize(
    `Endpoint ${formatEndpoint(
      context,
    )} removed empty query params (${keys}) from "${
      context?.url ?? ''
    }". The request URL stays clean. Omit null, undefined, and empty values in the endpoint definition.`,
    {
      endpoint: context?.endpoint,
      type: context?.type,
      method: context?.method,
      url: context?.url,
      removed: Object.fromEntries(removed.map(param => [param.key, param.value])),
    },
  );
};

/**
 * Drops empty query params and warns so the endpoint definition can be corrected.
 *
 * The returned object is a new value. The input is left unchanged.
 */
export const sanitizeQueryParams = (
  params: QueryParams | undefined,
  options?: SanitizeQueryParamsOptions,
): QueryParams | undefined => {
  if (params === null || params === undefined) {
    return undefined;
  }

  const { cleaned, removed } = partitionQueryParams(params);

  if (removed.length > 0 && options?.warn !== false) {
    warnEmptyParams(removed, options?.context);
  }

  if (Object.keys(cleaned).length === 0) {
    return undefined;
  }

  return cleaned;
};

const stringifyQueryParams = (params: QueryParams): string => {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    search.append(key, String(value));
  });

  return search.toString();
};

/**
 * Query-param serializer used by `fetchQuery`.
 *
 * Empty values are removed before the string is built. The warning is emitted by
 * {@link sanitizeQueryParams} when the enhancer normalizes the request. An empty
 * result means the caller must omit `params` so the URL has no trailing `?`.
 */
export const serializeQueryParams = (params: QueryParams): string => {
  const cleaned = sanitizeQueryParams(params, { warn: false });

  if (!cleaned) {
    return '';
  }

  return stringifyQueryParams(cleaned);
};

/**
 * Adds a trailing slash to an endpoint path and warns when the definition omitted
 * it.
 *
 * Query strings and hashes stay after the slash. URLs that already end with `/` are
 * left unchanged.
 */
export const ensureTrailingSlash = (
  url: string,
  context?: NormalizeLogContext,
): string => {
  const splitAt = url.search(QUERY_OR_HASH);
  const path = splitAt === -1 ? url : url.slice(0, splitAt);
  const suffix = splitAt === -1 ? '' : url.slice(splitAt);

  if (!path || path.endsWith('/')) {
    return url;
  }

  const normalizedUrl = `${path}/${suffix}`;

  warnNormalize(
    `Endpoint ${formatEndpoint(
      context,
    )} URL "${url}" is missing a trailing slash. The slash was added automatically. Add the trailing slash in the endpoint definition.`,
    {
      endpoint: context?.endpoint,
      type: context?.type,
      method: context?.method,
      url,
      normalizedUrl,
    },
  );

  return normalizedUrl;
};

const requestContext = (
  args: string | FetchArgs,
  context?: NormalizeLogContext,
): NormalizeLogContext => ({
  endpoint: context?.endpoint,
  type: context?.type,
  method: context?.method ?? (typeof args === 'string' ? undefined : args.method),
  url: context?.url ?? (typeof args === 'string' ? args : args.url),
});

/** Normalizes a fetchBaseQuery argument before the request is sent. */
export const normalizeFetchArgs = (
  args: string | FetchArgs,
  paramsSerializer: (params: QueryParams) => string = serializeQueryParams,
  context?: NormalizeLogContext,
): string | FetchArgs => {
  const details = requestContext(args, context);

  if (typeof args === 'string') {
    return ensureTrailingSlash(args, details);
  }

  if (args.params) {
    sanitizeQueryParams(args.params, { context: details });
  }

  const serializedParams = args.params ? paramsSerializer(args.params) : undefined;

  return produce(args, draft => {
    draft.url = ensureTrailingSlash(draft.url, details);

    if (!draft.params) {
      return;
    }

    if (!serializedParams) {
      delete draft.params;
      return;
    }

    Object.keys(draft.params).forEach(key => {
      if (isEmptyQueryParamValue(draft.params?.[key])) {
        delete draft.params?.[key];
      }
    });
  });
};
